/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { getThemeById, getDefaultTheme } from "@/lib/themes";
import type { OrganizationTheme } from "@/lib/themes";
import { isMarketingPage } from "@/lib/utils/domain/marketing-pages";
import { useMemo } from "react";
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "../constants";
import type { CachedOrgTheme } from "../types";

interface UseThemeColorsOptions {
  settings?: {
    primaryColor?: string | null;
    secondaryColor?: string | null;
    themeId?: string | null;
  } | null;
  cachedTheme: CachedOrgTheme | null;
  pathname: string;
}

/**
 * Hook to get theme configuration based on organization settings, cache, and marketing page detection
 *
 * Supports two modes:
 * 1. Theme Selection: Organization has selected a predefined theme (themeId)
 * 2. Custom Colors: Organization has custom primary/secondary colors (legacy/custom)
 */
export function useThemeColors({
  settings,
  cachedTheme,
  pathname,
}: UseThemeColorsOptions) {
  const isMarketing = useMemo(() => isMarketingPage(pathname), [pathname]);

  const hasCustomColors = Boolean(
    settings?.primaryColor && settings?.secondaryColor,
  );

  // Check if organization has selected a predefined theme
  const organizationTheme = useMemo<OrganizationTheme | null>(() => {
    // Use settings themeId, falling back to cached themeId for flash prevention
    const themeId = settings?.themeId ?? cachedTheme?.themeId;

    if (themeId) {
      return getThemeById(themeId) ?? getDefaultTheme();
    }

    // No themeId — fall back to default theme unless org uses legacy custom colors
    if (!hasCustomColors) {
      return getDefaultTheme();
    }

    return null;
  }, [hasCustomColors, settings?.themeId, cachedTheme?.themeId]);

  const primaryColor = useMemo(() => {
    // If using predefined theme, extract primary color from theme
    if (organizationTheme) {
      return organizationTheme.colors.primary.hex;
    }

    // Fallback to custom colors (legacy support)
    const orgPrimary = settings?.primaryColor;
    if (!orgPrimary && cachedTheme?.primaryColor) {
      return cachedTheme.primaryColor;
    }

    // Use defaults for marketing pages without org settings
    return orgPrimary ?? DEFAULT_PRIMARY_COLOR;
  }, [organizationTheme, settings?.primaryColor, cachedTheme]);

  const secondaryColor = useMemo(() => {
    // If using predefined theme, extract secondary color from theme
    if (organizationTheme) {
      return organizationTheme.colors.secondary.hex;
    }

    // Fallback to custom colors (legacy support)
    const orgSecondary = settings?.secondaryColor;
    if (!orgSecondary && cachedTheme?.secondaryColor) {
      return cachedTheme.secondaryColor;
    }

    // Use defaults for marketing pages without org settings
    return orgSecondary ?? DEFAULT_SECONDARY_COLOR;
  }, [organizationTheme, settings?.secondaryColor, cachedTheme]);

  return {
    primaryColor,
    secondaryColor,
    organizationTheme,
    isMarketing,
  };
}
